import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import FileUploader from "@/components/FileUploader";
import { FileText, ChevronDown, ChevronRight, Trash2, ExternalLink, Search, Filter } from "lucide-react";

const PURPOSE_LABELS = {
  verification_doc: "Verification Documents",
  invoice: "Invoices",
  proof_of_delivery: "Proof of Delivery",
  ticket_attachment: "Support Attachments",
  other: "Other",
};

const PURPOSE_ORDER = ["verification_doc", "invoice", "proof_of_delivery", "ticket_attachment", "other"];

export default function DocumentVault() {
  const { user, company } = useAuth();
  const [data, setData] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [filterPurpose, setFilterPurpose] = useState("all");
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/uploads/grouped"); setData(data); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleVersions = (id) => setOpen(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const remove = async (fid) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try { await api.delete(`/uploads/${fid}`); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const purposes = PURPOSE_ORDER.filter(p => data[p]?.length);
  const visiblePurposes = filterPurpose === "all" ? purposes : purposes.filter(p => p === filterPurpose);
  const totalDocs = Object.values(data).flat().length;

  return (
    <>
      <PageHeader
        title="Document Vault"
        subtitle={`Whole-company file vault · ${totalDocs} document${totalDocs === 1 ? "" : "s"} across ${user?.role === "admin" ? "all companies" : (company?.legal_name || "your company")}`}
      />

      {/* Quick-upload */}
      {user?.role !== "admin" && (
        <div style={{ marginBottom: 24 }}>
          <FileUploader purpose="other" onUploaded={load} label="Upload a document" />
        </div>
      )}

      {/* Toolbar */}
      <div className="vault-toolbar">
        <div className="vault-search">
          <Search size={14} color="var(--ink-mute)" />
          <input className="input" data-testid="vault-search" placeholder="Search filename or tag…" value={search} onChange={e => setSearch(e.target.value)} style={{ border: 0, padding: "6px 8px", background: "transparent" }} />
        </div>
        <div className="vault-filters">
          <Filter size={14} color="var(--ink-mute)" />
          <button onClick={() => setFilterPurpose("all")} data-testid="vault-filter-all" className={`btn ${filterPurpose === "all" ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>All</button>
          {PURPOSE_ORDER.filter(p => data[p]?.length).map(p => (
            <button key={p} onClick={() => setFilterPurpose(p)} data-testid={`vault-filter-${p}`} className={`btn ${filterPurpose === p ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>
              {PURPOSE_LABELS[p]} <span style={{ color: "var(--ink-mute)", marginLeft: 4 }}>{data[p].length}</span>
            </button>
          ))}
        </div>
      </div>

      {err && <div style={{ color: "var(--red)", marginBottom: 12 }}>{err}</div>}
      {busy && <div style={{ color: "var(--ink-mute)" }}>Loading…</div>}

      {!busy && totalDocs === 0 && (
        <div className="card" style={{ padding: 48, textAlign: "center", color: "var(--ink-mute)" }}>
          <FileText size={36} style={{ margin: "0 auto 12px", opacity: .4 }} />
          <div>No documents uploaded yet.</div>
        </div>
      )}

      {visiblePurposes.map(p => {
        const docs = data[p].filter(d => {
          if (!search) return true;
          const q = search.toLowerCase();
          const tagMatch = (d.tags || []).some(t => t.toLowerCase().includes(q));
          return (d.filename || "").toLowerCase().includes(q)
            || (d.original_filename || "").toLowerCase().includes(q)
            || tagMatch;
        });
        if (docs.length === 0) return null;
        return (
          <section key={p} className="vault-section" data-testid={`vault-group-${p}`}>
            <div className="vault-section-head">
              <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)" }}>{PURPOSE_LABELS[p].toUpperCase()}</div>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{docs.length} slot{docs.length === 1 ? "" : "s"}</div>
            </div>
            <div className="vault-list">
              {docs.map(doc => (
                <div key={doc.id} className="vault-row" data-testid={`vault-row-${doc.id}`}>
                  <div className="vault-row-main">
                    <FileText size={20} color="var(--amber-500)" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <a href={`${API_BASE}${doc.url}`} target="_blank" rel="noreferrer" data-testid={`vault-open-${doc.id}`} className="vault-filename">
                        {doc.original_filename || doc.filename}
                      </a>
                      <div className="vault-row-meta font-mono">
                        v{doc.version || 1} · {(doc.size / 1024).toFixed(1)} KB · {doc.mime_type || "—"} · {new Date(doc.created_at).toLocaleString()}
                        {doc.tags?.length > 0 && <span style={{ color: "var(--amber-400)", marginLeft: 8 }}>· {doc.tags.join(", ")}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {doc.version_count > 1 && (
                        <button data-testid={`vault-history-${doc.id}`} onClick={() => toggleVersions(doc.id)} className="btn btn-ghost" style={{ padding: "5px 10px", fontSize: 11 }}>
                          {open.has(doc.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {doc.version_count - 1} prior
                        </button>
                      )}
                      <a href={`${API_BASE}${doc.url}`} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ padding: "5px 10px", fontSize: 11 }}><ExternalLink size={12} /></a>
                      <button data-testid={`vault-delete-${doc.id}`} onClick={() => remove(doc.id)} className="btn btn-danger" style={{ padding: "5px 10px", fontSize: 11 }}><Trash2 size={12} /></button>
                    </div>
                  </div>
                  {open.has(doc.id) && doc.versions?.length > 0 && (
                    <div className="vault-versions">
                      {doc.versions.map(v => (
                        <div key={v.id} className="vault-version-row">
                          <FileText size={14} color="var(--ink-mute)" />
                          <a href={`${API_BASE}${v.url}`} target="_blank" rel="noreferrer" className="vault-version-name">
                            v{v.version} · {v.original_filename || v.filename}
                          </a>
                          <span className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>
                            {(v.size / 1024).toFixed(1)} KB · {new Date(v.created_at).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}
